import type { Edge, Node } from '@xyflow/react';

/**
 * Hierarchical top-down layout that knows about a node's exit order.
 *
 * dagre's network-simplex is great at minimising crossings in the
 * abstract, but it doesn't know that exit handles have a fixed left→
 * right ordering on the module card. The result is that two siblings
 * can end up swapped — a node connected to its parent's left exit
 * still gets drawn on the right — and unrelated nodes can land in the
 * same column as a long edge, so the edge appears to pass *through*
 * them.
 *
 * This layout fixes both:
 *
 *   1. We BFS the graph from the start node, assigning each visited
 *      node a primary parent (the one that first reaches it). Edges
 *      from non-primary parents still exist in React Flow but they
 *      don't influence positioning, which lets the layout stay
 *      tree-shaped even when the graph has loops or merge points.
 *
 *   2. Each node's children are sorted by the *exit index on the
 *      parent* (`moduleDef.exits` array order = visual left→right
 *      order of the exit handles). So a left-most exit's child is
 *      always placed under that exit, the right-most under that, etc.
 *
 *   3. Subtree-width is computed bottom-up; each node is then centred
 *      over its allocated horizontal band. That naturally pushes
 *      separate sub-branches into their own columns, which means a
 *      "long" edge skipping a rank (e.g. parent→grandchild) curves
 *      away from any intermediate sibling instead of running through
 *      its column.
 *
 * Disconnected components are laid out next to the main tree as
 * separate roots so they don't get lost off-screen.
 */
export interface AutoLayoutOpts {
  /** ID of the start/initial node; used as the BFS root. */
  startNodeId?: string;
  /** Horizontal gap between siblings (and between sub-tree spans). */
  nodeSep?: number;
  /** Vertical gap between successive ranks. */
  rankSep?: number;
  /** Default node width when React Flow hasn't measured the node yet. */
  defaultNodeWidth?: number;
  /** Default node height when React Flow hasn't measured the node yet. */
  defaultNodeHeight?: number;
  /** Extra horizontal gap between disconnected sub-graphs. */
  rootGap?: number;
}

type NodeWithModuleExits = {
  data?: {
    moduleDef?: {
      exits?: ReadonlyArray<{ exitId: number | string }>;
    };
  };
  measured?: { width?: number; height?: number };
  width?: number;
  height?: number;
};

export function autoLayoutTopDown<T extends Record<string, unknown>>(
  nodes: Node<T>[],
  edges: Edge[],
  opts: AutoLayoutOpts = {},
): Node<T>[] {
  if (nodes.length === 0) return nodes;

  const {
    startNodeId,
    nodeSep = 60,
    rankSep = 90,
    defaultNodeWidth = 240,
    defaultNodeHeight = 140,
    rootGap = 120,
  } = opts;

  const nodeMap = new Map(nodes.map((n) => [n.id, n]));

  // Position of `sourceHandle` within its source node's `moduleDef.exits`
  // array. Falls back to a large sentinel so handle-less edges (or edges
  // pointing at modules whose catalog entry we couldn't resolve) sink to
  // the right edge of the sort order rather than the left.
  function exitIdx(parentId: string, sourceHandle: string | null | undefined): number {
    if (!sourceHandle) return 9999;
    const parent = nodeMap.get(parentId) as NodeWithModuleExits | undefined;
    const exits = parent?.data?.moduleDef?.exits;
    if (!exits) return 9999;
    const i = exits.findIndex((x) => String(x.exitId) === sourceHandle);
    return i >= 0 ? i : 9999;
  }

  // children: source → list of (target, exitIdx), sorted by exitIdx so
  // the leftmost-exit child ends up first in the array.
  const children = new Map<string, Array<{ id: string; exitIdx: number }>>();
  for (const e of edges) {
    if (e.source === e.target) continue;
    if (!nodeMap.has(e.source) || !nodeMap.has(e.target)) continue;
    const arr = children.get(e.source) ?? [];
    arr.push({ id: e.target, exitIdx: exitIdx(e.source, e.sourceHandle) });
    children.set(e.source, arr);
  }
  for (const arr of children.values()) {
    arr.sort((a, b) => a.exitIdx - b.exitIdx);
  }

  // BFS from the start node (if any). Each node's primary parent is the
  // first one to reach it; that's what we use for positioning so the
  // layout stays tree-shaped even in the presence of merges/loops.
  const primaryParent = new Map<string, string | null>();
  const visited = new Set<string>();
  function bfs(root: string) {
    if (visited.has(root)) return;
    primaryParent.set(root, null);
    visited.add(root);
    const queue: string[] = [root];
    while (queue.length > 0) {
      const u = queue.shift()!;
      for (const { id: c } of children.get(u) ?? []) {
        if (visited.has(c)) continue;
        visited.add(c);
        primaryParent.set(c, u);
        queue.push(c);
      }
    }
  }
  if (startNodeId && nodeMap.has(startNodeId)) bfs(startNodeId);
  // Pick up disconnected components as additional roots.
  for (const n of nodes) bfs(n.id);

  // Build the tree-children map: only count edges where the target's
  // primary parent IS this source. That filters back-edges and merge
  // edges out of the positioning pass.
  const treeChildren = new Map<string, string[]>();
  for (const n of nodes) treeChildren.set(n.id, []);
  for (const [child, parent] of primaryParent.entries()) {
    if (parent != null) treeChildren.get(parent)!.push(child);
  }
  for (const [parent, kids] of treeChildren.entries()) {
    const order = new Map<string, number>();
    for (const c of children.get(parent) ?? []) order.set(c.id, c.exitIdx);
    kids.sort((a, b) => (order.get(a) ?? 9999) - (order.get(b) ?? 9999));
  }

  function nodeW(id: string): number {
    const n = nodeMap.get(id) as NodeWithModuleExits | undefined;
    return n?.measured?.width ?? n?.width ?? defaultNodeWidth;
  }
  function nodeH(id: string): number {
    const n = nodeMap.get(id) as NodeWithModuleExits | undefined;
    return n?.measured?.height ?? n?.height ?? defaultNodeHeight;
  }

  // Bottom-up subtree width: max(own-width, sum of children widths + gaps).
  const subW = new Map<string, number>();
  function computeSubW(id: string): number {
    const cached = subW.get(id);
    if (cached !== undefined) return cached;
    const kids = treeChildren.get(id) ?? [];
    if (kids.length === 0) {
      const w = nodeW(id);
      subW.set(id, w);
      return w;
    }
    const sum = kids.reduce((s, k) => s + computeSubW(k), 0) + (kids.length - 1) * nodeSep;
    const w = Math.max(nodeW(id), sum);
    subW.set(id, w);
    return w;
  }

  // Rank = BFS depth from the root of each tree. We need it so we can
  // give every rank a consistent y-coordinate (tallest node in the rank
  // sets the row height).
  const rank = new Map<string, number>();
  const roots = [...primaryParent.entries()]
    .filter(([, p]) => p === null)
    .map(([k]) => k);
  for (const root of roots) {
    rank.set(root, 0);
    const queue: string[] = [root];
    while (queue.length > 0) {
      const u = queue.shift()!;
      for (const c of treeChildren.get(u) ?? []) {
        rank.set(c, (rank.get(u) ?? 0) + 1);
        queue.push(c);
      }
    }
  }
  const maxRank = nodes.reduce((m, n) => Math.max(m, rank.get(n.id) ?? 0), 0);
  const maxHByRank = new Array(maxRank + 1).fill(0);
  for (const n of nodes) {
    const r = rank.get(n.id) ?? 0;
    const h = nodeH(n.id);
    if (h > maxHByRank[r]) maxHByRank[r] = h;
  }
  const rankY: number[] = new Array(maxRank + 1).fill(0);
  for (let r = 1; r <= maxRank; r++) {
    rankY[r] = rankY[r - 1] + maxHByRank[r - 1] + rankSep;
  }

  // Recursive placement: each node centred in its allocated band, its
  // tree-children laid out below in their own bands.
  const positions = new Map<string, { x: number; y: number }>();
  function place(id: string, leftX: number, allocatedWidth: number) {
    const r = rank.get(id) ?? 0;
    const w = nodeW(id);
    const cx = leftX + allocatedWidth / 2;
    positions.set(id, { x: cx - w / 2, y: rankY[r] });
    const kids = treeChildren.get(id) ?? [];
    if (kids.length === 0) return;
    const total =
      kids.reduce((s, k) => s + computeSubW(k), 0) + (kids.length - 1) * nodeSep;
    let cursor = cx - total / 2;
    for (const k of kids) {
      const kw = computeSubW(k);
      place(k, cursor, kw);
      cursor += kw + nodeSep;
    }
  }

  let rootCursor = 0;
  for (const root of roots) {
    const w = computeSubW(root);
    place(root, rootCursor, w);
    rootCursor += w + rootGap;
  }

  return nodes.map((n) => {
    const p = positions.get(n.id);
    if (!p) return n;
    return { ...n, position: p };
  });
}
